import { SUPPORTED_CLERK_EVENTS } from '@/lib/integrations/inbound/providers/clerk/event-types'
import { SUPPORTED_WORKOS_EVENTS } from '@/lib/integrations/inbound/providers/workos/event-types'
import { INTEGRATION_BRAND_DOMAIN } from '@/lib/integrations/integration-brand-domains'
import { SUPPORTED_STRIPE_EVENTS } from '@/lib/integrations/oauth/stripe/event-types'
import { SUPABASE_SETUP_SQL } from './supabase-setup-sql'
import type { IntegrationDefinition, IntegrationProvider } from './types'

const CLERK_EVENT_COUNT = SUPPORTED_CLERK_EVENTS.length
const STRIPE_EVENT_COUNT = SUPPORTED_STRIPE_EVENTS.length
const WORKOS_EVENT_COUNT = SUPPORTED_WORKOS_EVENTS.length

/**
 * Each `description` and `shortDescription` (and Framer's setup steps) is
 * also what the public `/browse/integrations` pages say in English
 * (`integrationsContent.providers`); `tests/integrations-copy-catalog.test.ts`
 * keeps the two equal.
 */
export const DATA_IN_INTEGRATION_DEFINITIONS = {
  framer: {
    name: 'Framer',
    brandDomain: INTEGRATION_BRAND_DOMAIN.framer,
    description:
      "Add your existing Framer forms to Brew. Submissions add or update contacts and can start automations using each form's trigger.",
    shortDescription: 'Forms and signups',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'Brew',
    lastEditedAt: 'October 12, 2026',
    dataDirection: 'data_in',
    integrationType: 'form_webhook',
    tutorialDocsUrl:
      'https://www.framer.com/help/articles/framer-form-webhook-setup/',
    tutorialSteps: [
      {
        label: 'Add your Framer form',
        description:
          'Select your brand, open the Framer integration in Brew, and choose Add form. Name your existing form and define the fields it submits. Each form becomes a Brew trigger.',
      },
      {
        label: 'Add the webhook in Framer',
        description:
          'Select your native form, open Send To, and add a Webhook. Paste the Brew URL and signing secret into the destination.',
      },
      {
        label: 'Match your field names',
        description:
          'Use the field name email for the required email input. For the other inputs, use the field names firstName and lastName, and the names Brew shows for any custom fields.',
      },
      {
        label: 'Publish and submit',
        description:
          'Publish your Framer site and submit the form. Brew receives the fields and adds or updates the contact, even without a published automation.',
      },
      {
        label: 'Connect an automation',
        description:
          'Open the form’s Automations tab and choose Create automation. Its trigger is selected for you. Publish the automation to run it on future submissions.',
      },
    ],
  },
  clerk: {
    name: 'Clerk',
    brandDomain: INTEGRATION_BRAND_DOMAIN.clerk,
    description:
      'Trigger Brew automations from Clerk webhook events for user signups, organization changes, paid subscriptions, and waitlist entries.',
    shortDescription: 'Auth event triggers',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'October 12, 2026',
    dataDirection: 'data_in',
    integrationType: 'webhook_signing_secret',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/clerk',
    testEventHint:
      'In Clerk, open Webhooks → your endpoint → Testing, pick user.created, and click Send Example. Clerk signs it automatically.',
    tutorialSteps: [
      {
        label: 'Generate your Brew webhook URL',
        description:
          'Click Generate webhook URL above to mint a unique endpoint for this brand. Copy it for the next step.',
        input: {
          type: 'copy',
          key: 'webhookUrl',
          placeholder:
            'Click "Generate webhook URL" above to mint your unique Brew endpoint',
        },
      },
      {
        label: 'Add an endpoint in Clerk',
        description:
          'Open dashboard.clerk.com → Webhooks → + Add Endpoint. Paste the Brew URL into the Endpoint URL field.',
      },
      {
        label: `Subscribe to the ${CLERK_EVENT_COUNT} events Brew supports`,
        description:
          'In Subscribe to events, enable the user, organization, organizationInvitation, organizationMembership, paymentAttempt, subscription, subscriptionItem, and waitlistEntry events listed under Clerk events below. Then click Create.',
      },
      {
        label: 'Reveal the Signing Secret',
        description:
          'On the endpoint detail page Clerk shows next, click the eye icon next to Signing Secret to reveal it. The value starts with whsec_.',
      },
      {
        label: 'Paste the Signing Secret below',
        description:
          'Paste the whsec_… secret and click Connect. Brew verifies every webhook with the Standard Webhooks (Svix) spec.',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'whsec_...',
        },
      },
      {
        label: 'Send an example event',
        description:
          'In Clerk, open Webhooks → your endpoint → Testing. Pick user.created and click Send Example. Clerk signs it automatically. Brew changes from Waiting for test event to Connected. An example only verifies the webhook: Brew adds no contact and starts no automation.',
      },
    ],
  },
  figma: {
    name: 'Figma',
    brandDomain: INTEGRATION_BRAND_DOMAIN.figma,
    description:
      'Turn Figma frames into pixel-accurate, responsive emails — paste a frame URL and Brew handles the rest.',
    shortDescription: 'Design-to-email',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'May 12, 2026',
    dataDirection: 'data_in',
    integrationType: 'oauth',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/figma',
    oAuthProvider: 'figma',
    tutorialSteps: [
      {
        label: 'Connect your Figma account',
        description:
          'Authorize Brew to access your Figma files, then paste a Figma frame URL into chat.',
      },
    ],
  },
  slack: {
    name: 'Slack',
    brandDomain: INTEGRATION_BRAND_DOMAIN.slack,
    description:
      'Mention @Brew in any Slack channel, thread or DM to work with Brew as yourself; every thread becomes a Brew chat. Brew also posts sent emails, a weekly overview, and comment mentions to the channels you choose.',
    shortDescription: 'Iterate on emails from Slack',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'Jan 21, 2026',
    dataDirection: 'data_in',
    integrationType: 'oauth',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/slack',
    oAuthProvider: 'slack',
    tutorialSteps: [
      {
        label: 'Connect your workspace',
        description:
          'An organization admin clicks Connect on this page and approves Brew in Slack. One install covers the whole organization.',
      },
      {
        label: 'Choose notification channels',
        description:
          'Pick the channels this brand posts to, and which updates each one gets: emails sent, the weekly overview, comment mentions and replies.',
      },
      {
        label: 'Mention @Brew',
        description:
          'Mention @Brew in a channel or DM it. The first time, Brew sends you a private link to connect your own Brew account; after that it answers in the thread as you. An organization admin can turn chatting off.',
      },
    ],
  },
  typeform: {
    name: 'Typeform',
    brandDomain: INTEGRATION_BRAND_DOMAIN.typeform,
    description:
      'Inform email copy with the latest Typeform responses — Brew suggests messaging that mirrors recent feedback.',
    shortDescription: 'Forms & surveys',
    author: 'Brew',
    availability: 'coming_soon',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'Dec 8, 2024',
    dataDirection: 'data_in',
    integrationType: 'oauth',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/typeform',
  },
  stripe: {
    name: 'Stripe',
    brandDomain: INTEGRATION_BRAND_DOMAIN.stripe,
    description: `Sync Stripe customers into the brand audience, trigger automations from ${STRIPE_EVENT_COUNT} Stripe events, and ship pre-built lifecycle emails (welcome, trial-end, failed-payment recovery, win-back).`,
    shortDescription: 'Customer sync + event triggers',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'Brew Team',
    lastEditedAt: 'October 12, 2026',
    dataDirection: 'data_in',
    integrationType: 'oauth',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/stripe',
    oAuthProvider: 'stripe',
    tutorialSteps: [
      {
        label: 'Click Connect with Stripe in the sidebar',
        description:
          'You will be redirected to Stripe to authorize Brew (one click). Stripe verifies your identity — Brew never sees your password.',
      },
      {
        label: 'Approve the OAuth scopes',
        description:
          'Brew requests read access to customers, subscriptions, invoices, charges, refunds, payment intents, quotes, and checkout sessions. See the Security tab for the full scope list. You can revoke access at any time from your Stripe dashboard.',
      },
      {
        label: 'Brew provisions trigger rows automatically',
        description: `After OAuth completes Brew registers a Connect webhook with Stripe and seeds the ${STRIPE_EVENT_COUNT} supported event rows (customer.*, customer.subscription.*, invoice.*, checkout.session.*, quote.*, charge.refunded, refund.created, payment_intent.payment_failed) as available triggers. No manual webhook setup or event enable switch is required. Only Live automations run.`,
      },
      {
        label: 'Publish an automation for the event you need',
        description:
          'Create an automation using a Stripe event and publish it to make it Live. In Manage, choose Sync customers from Stripe to import historical customers. The per-event Sync full name setting only controls name updates.',
      },
    ],
  },
  stytch: {
    name: 'Stytch',
    brandDomain: INTEGRATION_BRAND_DOMAIN.stytch,
    description:
      'Trigger Brew automations from direct Stytch Consumer user and B2B member events. Organization creation has no recipient; Dashboard and SCIM events do not start automations.',
    shortDescription: 'Auth event triggers',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'October 12, 2026',
    dataDirection: 'data_in',
    integrationType: 'webhook_signing_secret',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/stytch',
    testEventHint:
      'In Stytch, open Webhooks → your endpoint → Testing, pick direct.member.create (B2B) or direct.user.create (Consumer), and click Send Example. Stytch signs it automatically.',
    tutorialSteps: [
      {
        label: 'Generate your Brew webhook URL',
        description:
          'Click Generate webhook URL above to mint a unique endpoint for this brand. Copy it for step 3.',
        input: {
          type: 'copy',
          key: 'webhookUrl',
          placeholder:
            'Click "Generate webhook URL" above to mint your unique Brew endpoint',
        },
      },
      {
        label: 'Pick your Stytch project type',
        description:
          'Choose Consumer (B2C) or B2B. Brew filters to events for the selected vertical. Webhooks require the B2B Pro tier; Consumer ships on all paid Stytch plans.',
        input: {
          type: 'select',
          key: 'stytchProject',
          options: [
            { value: 'consumer', label: 'Consumer (B2C)' },
            { value: 'b2b', label: 'B2B' },
          ],
        },
      },
      {
        label: 'Add an endpoint in Stytch',
        description:
          'Open stytch.com/dashboard → Webhooks → + Add Endpoint. Paste the Brew URL into the Endpoint URL field.',
      },
      {
        label: 'Filter to the events Brew supports',
        description:
          'Consumer projects: enable direct.user.create / update / delete. B2B projects: enable direct.organization.create plus direct.member.create / update / delete. Click Create.',
      },
      {
        label: 'Reveal the Signing Secret',
        description:
          'On the endpoint settings page Stytch shows next, reveal the Signing Secret. The value starts with whsec_.',
      },
      {
        label: 'Paste the Signing Secret below',
        description:
          'Paste the whsec_… secret and click Connect. Brew verifies every webhook with the Standard Webhooks (Svix) spec.',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'whsec_...',
        },
      },
      {
        label: 'Send an example event',
        description:
          'In Stytch, open Webhooks → your endpoint → Testing. Pick direct.member.create (B2B) or direct.user.create (Consumer) and click Send Example. Stytch signs it automatically. Check its delivery status in Stytch, then confirm Brew changes from Waiting for test event to Connected. An example only verifies the webhook: Brew adds no contact and starts no automation.',
      },
    ],
  },
  supabase: {
    name: 'Supabase',
    brandDomain: INTEGRATION_BRAND_DOMAIN.supabase,
    description:
      'Trigger Brew automations from Supabase auth.users database webhooks, including derived events for email confirmation, password change, anonymous-to-real upgrade, and ban or unban.',
    shortDescription: 'Auth event triggers',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'October 12, 2026',
    dataDirection: 'data_in',
    integrationType: 'webhook_signing_secret',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/supabase',
    testEventHint:
      'After running the setup SQL and clicking Connect, create a test user in Supabase → Authentication → Users. The installed trigger automatically signs and sends the event.',
    tutorialSteps: [
      {
        label: 'Generate your Brew webhook URL',
        description:
          'Click Generate webhook URL above to mint a unique endpoint for this brand. The SQL in step 3 pre-fills it for you.',
        input: {
          type: 'copy',
          key: 'webhookUrl',
          placeholder:
            'Click "Generate webhook URL" above to mint your unique Brew endpoint',
        },
      },
      {
        label: 'Create your shared signing secret',
        description:
          'You own this secret; Brew and Supabase do not issue it. Run `openssl rand -hex 32`. It prints a 64-character hex string; paste that same value into the SQL block and the Connect form.',
      },
      {
        label: 'Run the setup SQL in Supabase',
        description:
          'In your Supabase project, open SQL Editor → New query. Paste the block below, replace `<PASTE_YOUR_SECRET_HERE>` with the secret from step 2, then click Run.',
        input: {
          type: 'code',
          language: 'sql',
          value: SUPABASE_SETUP_SQL,
        },
      },
      {
        label: 'Paste the same secret here',
        description:
          'Paste the secret from step 2 below, then click Connect. Brew uses it to verify every webhook your new trigger sends.',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'Same secret you pasted into the SQL block',
        },
      },
      {
        label: 'Create a test user to verify',
        description:
          'After you click Connect, create a test user in Supabase → Authentication → Users. The trigger the setup SQL installed signs and sends the event automatically. This page then changes from "Waiting for test event" to "Connected", and Manage → Recent events shows the delivery with "signature ok".',
      },
      {
        label: 'Find your Supabase triggers',
        description:
          'Connect created a trigger for every Supabase auth event; they are already on the Triggers page. Until a Live automation uses one, its events show "0 matched / 0 started" in Recent events. That is expected.',
      },
    ],
  },
  workos: {
    name: 'WorkOS',
    brandDomain: INTEGRATION_BRAND_DOMAIN.workos,
    description:
      'Trigger Brew automations from WorkOS user, authentication, password-reset, organization, directory-sync (SCIM), connection, and invitation events.',
    shortDescription: 'Enterprise auth triggers',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'October 12, 2026',
    dataDirection: 'data_in',
    integrationType: 'webhook_signing_secret',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/workos',
    testEventHint:
      'In WorkOS, open Webhooks → your endpoint → Send test event, pick user.created, and send it. WorkOS signs it automatically.',
    tutorialSteps: [
      {
        label: 'Generate your Brew webhook URL',
        description:
          'Click Generate webhook URL above to mint a unique endpoint for this brand. Copy it for the next step.',
        input: {
          type: 'copy',
          key: 'webhookUrl',
          placeholder:
            'Click "Generate webhook URL" above to mint your unique Brew endpoint',
        },
      },
      {
        label: 'Create an endpoint in WorkOS',
        description:
          'Open dashboard.workos.com → Webhooks → Create Endpoint. Paste the Brew URL into the Endpoint URL field.',
      },
      {
        label: `Subscribe to the ${WORKOS_EVENT_COUNT} events Brew supports`,
        description:
          'Enable user.*, authentication.email_verification_succeeded, authentication.magic_auth_succeeded, authentication.password_failed, password_reset.succeeded, organization.*, organization_membership.*, dsync.user.*, connection.activated, and invitation.accepted, as listed under WorkOS events below. Do not select authentication.*: it adds authentication events Brew does not support. Then click Create.',
      },
      {
        label: 'Copy the endpoint Signing Secret',
        description:
          'WorkOS reveals the per-endpoint signing secret on the endpoint detail page. Copy it. The value is opaque and has no prefix.',
      },
      {
        label: 'Paste the Signing Secret below',
        description:
          'Paste the secret and click Connect. Brew verifies every webhook with the WorkOS `t=<ts>,v1=<hmac>` envelope, 180s timestamp tolerance.',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'WorkOS endpoint signing secret',
        },
      },
      {
        label: 'Send a test event',
        description:
          'In WorkOS, open Webhooks → your endpoint → Send test event. Pick user.created and send it. WorkOS signs it automatically. Brew changes from Waiting for test event to Connected. Brew may process the test like a real event, so send it before you publish an automation on that trigger.',
      },
    ],
  },
  shopify: {
    name: 'Shopify',
    brandDomain: INTEGRATION_BRAND_DOMAIN.shopify,
    description:
      'Trigger Brew automations from Shopify customer, order, checkout, and fulfillment events. Powers welcome, receipt, abandoned-checkout, and shipping flows.',
    shortDescription: 'Commerce event triggers',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'October 12, 2026',
    dataDirection: 'data_in',
    integrationType: 'webhook_signing_secret',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/shopify',
    testEventHint:
      "In Shopify Admin → Settings → Notifications → Webhooks, open a saved webhook's … menu and click Send test. Shopify signs it automatically.",
    tutorialSteps: [
      {
        label: 'No client ID is required',
        description:
          'This setup uses Shopify Admin webhooks, not a Shopify app. You do not need a client ID, client secret, or OAuth app to connect this version of the integration.',
      },
      {
        label: 'Generate your Brew webhook URL',
        description:
          'Click Generate webhook URL above to mint a unique endpoint for this brand. Copy it for step 5.',
        input: {
          type: 'copy',
          key: 'webhookUrl',
          placeholder:
            'Click "Generate webhook URL" above to mint your unique Brew endpoint',
        },
      },
      {
        label: 'Enter your myshopify.com shop domain',
        description:
          'Use the canonical myshopify.com host (e.g. acme.myshopify.com), NOT your custom storefront domain. Brew matches this against the X-Shopify-Shop-Domain header to block cross-shop replay attacks.',
        input: {
          type: 'text',
          key: 'domain',
          placeholder: 'acme.myshopify.com',
        },
      },
      {
        label: 'Open Shopify Admin → Settings → Notifications',
        description:
          'Click Settings (gear icon, bottom-left of Shopify admin) → Notifications. Scroll to the Webhooks section.',
      },
      {
        label: 'Create a webhook for each event you want',
        description:
          'Shopify sends an event only if it has its own webhook, so a trigger fires only for topics you add a webhook for here. For each event you plan to use, click Create webhook, choose the event by the name shown after "In Shopify" in Shopify events below, set Format to JSON, paste the Brew URL, pick the latest API version, and Save. Do not add carts/update (Cart update): its payload has no recipient email.',
      },
      {
        label: 'Copy the per-shop signing secret',
        description:
          'Scroll to the bottom of the Webhooks section. Copy the long secret under "Your webhooks will be signed with…". The same secret signs every webhook on this shop.',
      },
      {
        label: 'Paste the signing secret below',
        description:
          'Paste the secret and click Connect. Brew verifies every webhook with base64(HMAC-SHA-256) against the X-Shopify-Hmac-SHA256 header.',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'Shopify per-shop webhook signing secret',
        },
      },
      {
        label: 'Send a test from each webhook',
        description:
          'In Shopify Admin → Settings → Notifications → Webhooks, open the … menu on each webhook you created and click Send test. Shopify signs it automatically. The first test changes this page from Waiting for test event to Connected; each test marks its event as received in Shopify events below. A test only verifies the webhook: Brew adds no contact and starts no automation.',
      },
    ],
  },
  revenuecat: {
    name: 'RevenueCat',
    brandDomain: INTEGRATION_BRAND_DOMAIN.revenuecat,
    description:
      'Trigger Brew automations from RevenueCat in-app purchase, subscription, and billing events across Apple, Google, Amazon, Stripe, and Web Billing. Requires the RevenueCat Pro plan or above.',
    shortDescription: 'IAP + subscription events',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'October 12, 2026',
    dataDirection: 'data_in',
    integrationType: 'webhook_signing_secret',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/revenuecat',
    tutorialSteps: [
      {
        label: 'Generate your Brew webhook URL',
        description:
          'Click Generate webhook URL above to mint a unique endpoint for this brand. RevenueCat webhooks require the Pro plan or above.',
        input: {
          type: 'copy',
          key: 'webhookUrl',
          placeholder:
            'Click "Generate webhook URL" above to mint your unique Brew endpoint',
        },
      },
      {
        label: 'Create the webhook configuration',
        description:
          'In RevenueCat, open Integrations → Webhooks → Add new configuration. Paste the Brew Webhook URL, choose Production, Sandbox, or both, and select the lifecycle events you want to send.',
      },
      {
        label: 'Enable HMAC webhook signing',
        description:
          'Toggle HMAC webhook signing in that RevenueCat webhook configuration. Copy the signing secret immediately: it is shown only once at creation or rotation. Brew verifies X-RevenueCat-Webhook-Signature against the exact raw request body.',
      },
      {
        label: 'Paste the signing secret here',
        description:
          'Paste the HMAC signing secret and click Connect. Do not paste a RevenueCat REST API key or the optional Authorization-header value. Existing Authorization-only Brew connections remain supported temporarily.',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'RevenueCat webhook signing secret',
        },
      },
      {
        label: 'Send a RevenueCat TEST event',
        description:
          'Use Send test webhook in RevenueCat. A signed TEST delivery verifies and activates the Brew connection; TEST itself does not trigger a customer automation. Real supported lifecycle events do.',
      },
      {
        label: 'Set the customer email attribute',
        description:
          'Set RevenueCat subscriber attribute $email for each customer so Brew can deliver automation emails. Brew also accepts an email-shaped app_user_id; otherwise the event is recorded but delivery uses a non-sendable synthetic address.',
      },
    ],
  },
} satisfies Partial<Record<IntegrationProvider, IntegrationDefinition>>
